import { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Typography,
  Tabs,
  Row,
  Col,
  Image,
  message,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import JobModal from "./JobModal";
import DepartmentModal from "./DepartmentModal";
import { formatDate } from "../../helpers";
import blink from "../../assets/images/blink.png";
import {
  Job,
  Department,
  DepartmentFormValues,
  Pagination,
  ListResponse,
  JobFormValues,
} from "../../types";
import { jobService } from "../../services";

const { Text } = Typography;

const JobsManagement = () => {
  const [form] = Form.useForm();
  const [departmentForm] = Form.useForm();

  const jobColumns = [
    {
      title: "Vị trí",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Địa điểm",
      dataIndex: "location",
      key: "location",
    },
    {
      title: "Mức lương",
      dataIndex: "salary",
      key: "salary",
      // API cho phép salary/expiredAt = null (vd. job tạo qua Swagger)
      render: (salary: { amount: number; unit: string } | null) => {
        return salary?.amount && salary?.unit ? `${salary.amount} ${salary.unit}` : '-';
      },
    },
    {
      title: "Lương tối thiểu",
      dataIndex: "minSalary",
      key: "minSalary",
      render: (minSalary?: number | null) => minSalary ?? "-",
    },
    {
      title: "Số lượng",
      dataIndex: "candidatesNumber",
      key: "candidatesNumber",
    },
    {
      title: "Ngày hết hạn",
      dataIndex: "expiredAt",
      key: "expiredAt",
      render: (expiredAt: string | null) =>
        expiredAt ? formatDate(expiredAt) : "-",
    },
    {
      title: "Trạng thái",
      dataIndex: "published",
      key: "published",
      render: (published: boolean) => {
        if (published)
          return (
            <Text style={{ color: "#52c41a", fontWeight: 500 }}>Hiển thị</Text>
          );
        return <Text style={{ color: "#ff4d4f", fontWeight: 500 }}>Ẩn</Text>;
      },
    },
    {
      title: "Phòng ban",
      dataIndex: "department",
      key: "department",
      render: (department: Department) => {
        return department.name;
      },
    },
    {
      title: "",
      key: "actions",
      render: (_: any, record: Job) => (
        <Space>
          <Button
            icon={<EditOutlined style={{ paddingLeft: "4px" }} />}
            onClick={() => showJobModal(record)}
          />
          <Button
            icon={<DeleteOutlined style={{ paddingLeft: "4px" }} />}
            danger
            onClick={() => handleJobDelete(record)}
          />
        </Space>
      ),
    },
  ];

  const departmentColumns = [
    {
      title: "Tên",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Trạng thái",
      dataIndex: "isHidden",
      key: "isHidden",
      render: (isHidden: boolean) => {
        if (isHidden) {
          return <Text style={{ color: "#ff4d4f", fontWeight: 500 }}>Ẩn</Text>;
        }
        return (
          <Text style={{ color: "#52c41a", fontWeight: 500 }}>Hiển thị</Text>
        );
      },
    },
    {
      title: "",
      key: "actions",
      render: (_: any, record: Department) => (
        <Space>
          <Button
            icon={<EditOutlined style={{ paddingLeft: "4px" }} />}
            onClick={() => showDepartmentModal(record)}
          />
          <Button
            icon={<DeleteOutlined style={{ paddingLeft: "4px" }} />}
            danger
            onClick={() => handleDepartmentDelete(record)}
          />
        </Space>
      ),
    },
  ];

  // Job states
  const [isJobModalVisible, setIsJobModalVisible] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [isDeleteVisible, setIsDeleteVisible] = useState(false);
  const [jobsData, setJobsData] = useState<ListResponse<Job>>();
  const [allDepartments, setAllDepartments] = useState<Department[]>([]);
  const [isLoadingJob, setIsLoadingJob] = useState(false);
  const [jobsPagination, setJobsPagination] = useState<Pagination>({
    page: 1,
    resultsPerPage: 10,
  });

  // Department states
  const [isDepartmentModalVisible, setIsDepartmentModalVisible] =
    useState(false);
  const [isDepartmentDeleteVisible, setIsDepartmentDeleteVisible] =
    useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(
    null
  );
  const [departmentsData, setDepartmentsData] =
    useState<ListResponse<Department>>();
  const [isLoadingDepartment, setIsLoadingDepartment] = useState(false);
  const [departmentsPagination, setDepartmentsPagination] =
    useState<Pagination>({
      page: 1,
      resultsPerPage: 10,
    });

  // Jobs functions
  const getJobList = useCallback(async () => {
    try {
      setIsLoadingJob(true);
      const response = await jobService.getJobList(jobsPagination);
      setJobsData(response);
    } catch (error) {
      message.error("Lỗi khi lấy danh sách việc làm: " + error);
    } finally {
      setIsLoadingJob(false);
    }
  }, [jobsPagination]);

  useEffect(() => {
    getJobList();
  }, [getJobList]);

  const handleJobsPaginationChange = (page: number, pageSize: number) => {
    setJobsPagination({
      ...jobsPagination,
      page,
      resultsPerPage: pageSize,
    });
  };

  const handleJobSearch = (value: string) => {
    setJobsPagination({
      ...jobsPagination,
      page: 1,
      search: value,
    });
  };

  const getAllDepartments = useCallback(async () => {
    try {
      if (
        departmentsData &&
        departmentsData?.totalResults > departmentsData?.data?.length
      ) {
        const response = await jobService.getDepartmentList({
          page: 1,
          resultsPerPage: departmentsData?.totalResults,
        });
        setAllDepartments(response.data);
      } else {
        setAllDepartments(departmentsData?.data || []);
      }
    } catch (error) {
      console.error("Lỗi khi lấy danh sách phòng ban: " + error);
    }
  }, [departmentsData]);

  useEffect(() => {
    getAllDepartments();
  }, [getAllDepartments]);

  const confirmJobDelete = async () => {
    try {
      if (!editingJob) return;
      await jobService.deleteJob(editingJob.id);
      message.success(`Xoá việc làm ${editingJob.title} thành công`);
    } catch (error) {
      message.error("Lỗi khi xoá việc làm: " + error);
    } finally {
      setIsDeleteVisible(false);
      setEditingJob(null);
      await getJobList();
    }
  };

  const handleJobSubmit = async (values: JobFormValues) => {
    try {
      if (editingJob) {
        await jobService.updateJob(editingJob.id, values);
        message.success(`Cập nhật việc làm ${values.title} thành công`);
      } else {
        await jobService.createJob(values);
        message.success(`Thêm việc làm ${values.title} thành công`);
      }
      handleCancelJobModal();
      await getJobList();
    } catch (error) {
      message.error("Lỗi khi thêm việc làm: " + error);
    }
  };

  const showJobModal = async (record: Job | null = null) => {
    if (record) {
      const job = await jobService.getJobById(record.id);
      form.setFieldsValue({
        ...job,
        expiredAt: job.expiredAt ? dayjs(job.expiredAt) : undefined,
        departmentId: job.department.id,
      });
      setEditingJob(job);
    } else {
      form.resetFields();
      setEditingJob(null);
    }
    setIsJobModalVisible(true);
  };

  const handleCancelJobModal = () => {
    setIsJobModalVisible(false);
    form.resetFields();
    setEditingJob(null);
  };

  const handleJobDelete = (record: Job) => {
    setEditingJob(record);
    setIsDeleteVisible(true);
  };

  // Departments functions
  const getDepartmentList = useCallback(async () => {
    try {
      setIsLoadingDepartment(true);
      const response = await jobService.getDepartmentList(
        departmentsPagination
      );
      setDepartmentsData(response);
    } catch (error) {
      message.error("Lỗi khi lấy danh sách phòng ban: " + error);
    } finally {
      setIsLoadingDepartment(false);
    }
  }, [departmentsPagination]);

  useEffect(() => {
    getDepartmentList();
  }, [getDepartmentList]);

  const handleDepartmentsPaginationChange = (
    page: number,
    pageSize: number
  ) => {
    setDepartmentsPagination({
      ...departmentsPagination,
      page,
      resultsPerPage: pageSize,
    });
  };

  const handleDepartmentSearch = (value: string) => {
    setDepartmentsPagination({
      ...departmentsPagination,
      page: 1,
      search: value,
    });
  };

  const showDepartmentModal = (record: Department | null = null) => {
    if (record) {
      departmentForm.setFieldsValue(record);
      setEditingDepartment(record);
    } else {
      departmentForm.resetFields();
      setEditingDepartment(null);
    }
    setIsDepartmentModalVisible(true);
  };

  const handleCancelDepartmentModal = () => {
    setIsDepartmentModalVisible(false);
    departmentForm.resetFields();
    setEditingDepartment(null);
  };

  const handleDepartmentDelete = async (record: Department) => {
    setEditingDepartment(record);
    setIsDepartmentDeleteVisible(true);
  };

  const confirmDepartmentDelete = async () => {
    try {
      if (!editingDepartment) return;
      await jobService.deleteDepartment(editingDepartment.id);
      message.success(`Xoá phòng ban ${editingDepartment.name} thành công`);
    } catch (error) {
      message.error("Lỗi khi xoá phòng ban: " + error);
    } finally {
      setIsDepartmentDeleteVisible(false);
      setEditingDepartment(null);
      await getDepartmentList();
    }
  };

  const handleDepartmentSubmit = async (values: DepartmentFormValues) => {
    try {
      if (editingDepartment) {
        await jobService.updateDepartment(editingDepartment.id, values);
        message.success(`Cập nhật phòng ban ${values.name} thành công`);
      } else {
        await jobService.createDepartment(values);
        message.success(`Thêm phòng ban ${values.name} thành công`);
      }
      handleCancelDepartmentModal();
      await getDepartmentList();
    } catch (error) {
      message.error("Lỗi khi thêm phòng ban: " + error);
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <h1 className="page-title">
        Quản lý việc làm <Image src={blink} preview={false} className="blink-icon" />
      </h1>
      <Tabs defaultActiveKey="positions" type="card">
        <Tabs.TabPane tab="Việc làm" key="positions">
          <Row
            style={{
              marginBottom: 16,
            }}
          >
            <Col
              xs={24}
              sm={15}
              md={12}
              lg={20}
              xl={20}
              style={{ marginTop: 10 }}
            >
              <Input.Search
                placeholder="Tìm kiếm"
                onChange={(e) => handleJobSearch(e.target.value)}
                style={{ maxWidth: 500 }}
                className="job-search-input"
              />
            </Col>
            <Col
              xs={24}
              sm={9}
              md={12}
              lg={4}
              xl={4}
              style={{ textAlign: "right", marginTop: 10 }}
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => showJobModal()}
              >
                Thêm
              </Button>
            </Col>
          </Row>

          <Table
            rowKey="id"
            columns={jobColumns}
            dataSource={jobsData ? jobsData.data : []}
            pagination={{
              current: jobsPagination.page,
              pageSize: jobsPagination.resultsPerPage,
              total: jobsData ? jobsData.totalResults : 0,
              onChange: handleJobsPaginationChange,
            }}
            scroll={{ x: "max-content" }}
            loading={isLoadingJob}
          />
        </Tabs.TabPane>

        <Tabs.TabPane tab="Phòng ban" key="departments">
          <Row
            style={{
              marginBottom: 16,
            }}
          >
            <Col
              xs={24}
              sm={15}
              md={12}
              lg={20}
              xl={20}
              style={{ marginTop: 10 }}
            >
              <Input.Search
                placeholder="Tìm kiếm"
                onChange={(e) => handleDepartmentSearch(e.target.value)}
                style={{ maxWidth: 500 }}
                className="job-search-input"
              />
            </Col>
            <Col
              xs={24}
              sm={9}
              md={12}
              lg={4}
              xl={4}
              style={{ textAlign: "right", marginTop: 10 }}
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => showDepartmentModal()}
              >
                Thêm
              </Button>
            </Col>
          </Row>

          <Table
            rowKey="id"
            columns={departmentColumns}
            dataSource={departmentsData ? departmentsData.data : []}
            pagination={{
              current: departmentsPagination.page,
              pageSize: departmentsPagination.resultsPerPage,
              total: departmentsData ? departmentsData.totalResults : 0,
              onChange: handleDepartmentsPaginationChange,
            }}
            scroll={{ x: "max-content" }}
            loading={isLoadingDepartment}
          />
        </Tabs.TabPane>
      </Tabs>

      {/* Job Modal */}
      <JobModal
        visible={isJobModalVisible}
        form={form}
        onFinish={handleJobSubmit}
        onCancel={handleCancelJobModal}
        editingRecord={editingJob}
        departments={allDepartments}
      />

      {/* Department Modal */}
      <DepartmentModal
        visible={isDepartmentModalVisible}
        form={departmentForm}
        onFinish={handleDepartmentSubmit}
        onCancel={handleCancelDepartmentModal}
        editingRecord={editingDepartment}
      />

      {/* Job Delete Modal */}
      <Modal
        title="Xoá việc làm"
        open={isDeleteVisible}
        onOk={confirmJobDelete}
        onCancel={() => setIsDeleteVisible(false)}
        okText="Xoá"
        okButtonProps={{ danger: true }}
        cancelText="Hủy"
      >
        Bạn có chắc chắn muốn xoá việc làm {editingJob?.title} không?
      </Modal>

      {/* Department Delete Modal */}
      <Modal
        title="Xoá phòng ban"
        open={isDepartmentDeleteVisible}
        onOk={confirmDepartmentDelete}
        onCancel={() => setIsDepartmentDeleteVisible(false)}
        okText="Xoá"
        okButtonProps={{ danger: true }}
        cancelText="Hủy"
      >
        Bạn có chắc chắn muốn xoá phòng ban {editingDepartment?.name} không?
      </Modal>
    </div>
  );
};

export default JobsManagement;
